import type {FormController} from './types';
import type {Observable} from 'rxjs';
import {BehaviorSubject, combineLatest} from 'rxjs';
import type {Validator} from 'svelte-forms';
import {CompositeFormController} from './composite-form-controller';
import type {Modifier} from './types';

export class FormArrayBuilder<T> {
    private formControllers: FormController<T>[] = [];
    private validators: Validator[] = [];
    private modifiers: Modifier[] = [];

    public registerFormControl(formController: FormController<T>): void {
        this.formControllers.push(formController);
    }

    public addValidators(validators: Validator[]): FormArrayBuilder<T> {
        this.validators = [...this.validators, ...validators];
        return this;
    }

    public addModifiers(modifiers: Modifier[]): FormArrayBuilder<T> {
        this.modifiers = [...this.modifiers, ...modifiers];
        return this;
    }

    public getLastRegisteredFormController(): FormController<T> | null {
        if (this.formControllers.length === 0) {
            return null;
        }
        return this.formControllers[this.formControllers.length - 1];
    }

    public build(id: string, name: string, emptyAsNull: boolean, editorNameToFieldNameMap: Map<string, number>): FormArray<T> {
        return new FormArray<T>(id, name, [...this.formControllers], [...this.validators], [...this.modifiers], emptyAsNull, editorNameToFieldNameMap);
    }
}

export class FormArray<T> extends CompositeFormController<T[], T> {

    constructor(id: string,
                name: string,
                formControllers: FormController<T>[],
                validators: Validator[],
                modifiers: Modifier[],
                private emptyAsNull: boolean,
                private editorNameToFieldIndexMap: Map<string, number>) {
        super(id, name, formControllers, validators, modifiers);
        this.initializeController();
    }

    public addFieldAt(index: number, newFormController: FormController<T>): void {
        this.editorNameToFieldIndexMap.set(newFormController.name, index);
        this.addController(newFormController)
    }

    public removeFieldAt(toBeRemovedIndex: number): void {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [toBeRemovedFieldName] = [...this.editorNameToFieldIndexMap.entries()].find(([_, index]) => toBeRemovedIndex === index);
        this.removeControllerByName(toBeRemovedFieldName);

        this.editorNameToFieldIndexMap.delete(toBeRemovedFieldName);
        this.editorNameToFieldIndexMap.forEach((index, name, map) => index > toBeRemovedIndex ? map.set(name, index - 1) : undefined)
    }

    public setModelValue(newModelValue: T[]) {
        this.formControllers.forEach((value) => {
            if (newModelValue !== null) {
                value.setModelValue(newModelValue[this.editorNameToFieldIndexMap.get(value.name)])
            }
        })
    }

    protected combineToModelValue$(formControllers: FormController<T>[]): Observable<T[]> {
        if (formControllers.length === 0) {
            return new BehaviorSubject(this.emptyAsNull ? null : []).asObservable();
        }

        const modelValueStreams = formControllers
            .map((controller) => ({
                index: this.editorNameToFieldIndexMap.get(controller.name),
                modelValue$: controller.getModelValue$()
            }) as const)
            .sort(({index: index1}, {index: index2}) => index1 - index2)
            .map(({modelValue$}) => modelValue$);

        return combineLatest(modelValueStreams);
    }

    protected getModelFromStateValue(stateValue: Record<string, T>): T[] {
        return this.formControllers.map((controller) => ({
            index: this.editorNameToFieldIndexMap.get(controller.name),
            model: stateValue[controller.name]
        }) as const)
            .sort(({index: index1}, {index: index2}) => index1 - index2)
            .map(({model}) => model);
    }
}